import React, { useState } from 'react';
import { Download, X, FileText, Image as ImageIcon, Package } from 'lucide-react';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemIds: string[];
  itemCount: number;
}

export default function ExportModal({ isOpen, onClose, itemIds, itemCount }: ExportModalProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  if (!isOpen) return null;

  const handleExport = async () => {
    console.log('Exporting images and RTF for:', itemIds);
    setIsExporting(true);
    setProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => (prev >= 90 ? 90 : prev + 10));
      }, 200);

      // Use GET with query params for combined export (images + RTF)
      const response = await fetch(`/api/export-combined?itemIds=${itemIds.join(',')}`);
      clearInterval(progressInterval);

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      setProgress(100);

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch?.[1] || `export_${itemCount}items_${Date.now()}.zip`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setTimeout(() => {
        alert(`Export complete!\nExported images and listings for ${itemCount} items`);
        onClose();
        setIsExporting(false);
        setProgress(0);
      }, 500);
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
      setIsExporting(false);
      setProgress(0);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1A1F2E, #242938)',
        border: '2px solid #FF6B35',
        boxShadow: '0 0 40px rgba(255, 107, 53, 0.4)',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '500px',
        padding: '32px',
        position: 'relative'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isExporting}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            color: '#8A95AA',
            cursor: isExporting ? 'not-allowed' : 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            opacity: isExporting ? 0.5 : 1
          }}
        >
          <X size={24} />
        </button>

        {/* Title */}
        <h2 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '28px',
          fontWeight: 700,
          color: '#FF6B35',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          Export Items
        </h2>

        <p style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '16px',
          color: '#8A95AA',
          marginBottom: '24px'
        }}>
          {itemCount} item{itemCount > 1 ? 's' : ''} selected
        </p>

        {/* Export Action */}
        <div style={{
          marginBottom: '24px'
        }}>
          <button
            onClick={handleExport}
            disabled={isExporting}
            style={{
              width: '100%',
              padding: '16px 24px',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: 700,
              fontSize: '18px',
              textTransform: 'uppercase',
              background: isExporting ? '#334155' : 'linear-gradient(135deg, #FF6B35, #F97316)',
              border: '2px solid #FF6B35',
              color: '#FFFFFF',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              transition: 'all 0.2s',
              boxShadow: isExporting ? 'none' : '0 0 20px rgba(255, 107, 53, 0.3)'
            }}
          >
            <Download size={24} />
            <span>{isExporting ? 'Exporting...' : 'Export All (Images + Listings)'}</span>
          </button>
        </div>

        {/* Progress bar */}
        {isExporting && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              background: '#1A1F2E',
              border: '1px solid #334155',
              borderRadius: '4px',
              height: '32px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <div style={{
                background: 'linear-gradient(90deg, #FF6B35, #F97316)',
                height: '100%',
                width: `${progress}%`,
                transition: 'width 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)'
                }}>
                  {progress}%
                </span>
              </div>
            </div>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              marginTop: '8px',
              textAlign: 'center'
            }}>
              {progress < 100 ? 'Generating export package...' : 'Download starting...'}
            </p>
          </div>
        )}

        {/* Close button */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={isExporting}
            style={{
              padding: '12px 24px',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              textTransform: 'uppercase',
              background: 'transparent',
              border: '2px solid #8A95AA',
              color: '#8A95AA',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              opacity: isExporting ? 0.5 : 1,
              transition: 'all 0.2s'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
